import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Arvika?", a: "Ansökan görs till tillståndsenheten i Arvika kommun. Verksamhet i annan kommun i Värmland (t.ex. Karlstad, Eda, Årjäng, Sunne) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Arvika?", a: "Arvika kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Arvika?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd." },
];

export const Route = createFileRoute("/serveringstillstand-arvika")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Arvika" },
      ]}
      title="Serveringstillstånd Arvika – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Arvika: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.arvika, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Arvika?", body: "Tillståndsenheten i Arvika kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Eda, Sunne, Årjäng eller Karlstad ansöker du hos respektive kommun." },
        { h2: "Krav från Arvika kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Arvika en aktiv evenemangs- och festivalnäring vid Kyrkviken. Tillfälliga tillstånd är vanliga sommartid. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Arvika", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Sök i god tid inför festivalsäsongen." },
        { h2: "Tillsyn och uppföljning", body: "Arvika kommun bedriver tillsyn tillsammans med polis och Skatteverket, särskilt under stora evenemang. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – festival- och evenemangsstad", body: "Arvika är känt för sin evenemangs- och kulturprofil vid Kyrkviken. Många serveringsställen jobbar med kombinationen restaurang + livemusik – tänk på ordningsvillkor, ljudnivåer och tider redan i ansökan." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-karlstad", label: "Serveringstillstånd Karlstad" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsbank/serveringstillstand-festival", label: "Festival – serveringstillstånd" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Arvika – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Arvika: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Arvika – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Arvika." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-arvika" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-arvika" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Arvika", url: "https://alkoholprovet.se/serveringstillstand-arvika" },
      ]),
    ],
  }),
});
